import { axiosInstance } from 'utils';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import { datadogLogs } from '@datadog/browser-logs';
import { useApplication, useClient } from 'hooks/webapi/index';
import axios, { AxiosError } from 'axios';
import useActiveCard from './useActiveCard';
import useError from 'hooks/useError';
import useLatestLoan from './useLatestLoan';

type Props = {
  children: ReactNode;
};

type BizumPaymentsOptions = {
  isExtension?: boolean;
  body: {
    amount?: number;
    successUrl: string;
    failureUrl: string;
    extensionTerm?: number;
  };
};

type CardPaymentsOptions = {
  body: {
    amount: number;
    applicationName: string;
    username: string;
    loanNumber: string;
    currency: string;
    storeCreditCard: boolean;
    successUrl: string;
    failureUrl: string;
    extensionTerm?: number;
  };
};

type CardPaymentsReissuedOptions = {
  body: {
    amount: number;
    applicationName: string;
    username: string;
    loanNumber: string;
    currency: string;
    cardId: number;
    extensionTerm?: number;
  };
};

type SaveCardPayments = {
  body: {
    successUrl: string;
    failureUrl: string;
  };
};

type BizumPaymentsResponse = {
  errorCode?: 'DUPLICATE_PAYMENT' | string;
  status: 'OK' | 'ERROR';
  payFormUrl: string;
  requestId: string;
};

export type CardPaymentsResponse = {
  status: string;
  payformUrl?: string;
  redirectUrl?: string;
  errorMessage?: null | string;
  errorMessageTemplate?: string;
};

export type CardPaymentsReissuedResponse = {
  status: string;
  payformUrl?: string;
  redirectUrl?: string;
  errorMessage?: null | string;
  errorMessageTemplate?: string;
};

type SavedCardResponse = {
  status: string;
  payformUrl: string;
  errorMessage: null | string;
};

type CardPaymentsContextType = {
  getBizumPayments: (
    props: BizumPaymentsOptions,
  ) => Promise<BizumPaymentsResponse | void>;
  postCardPayments: ({
    body,
  }: CardPaymentsOptions) => Promise<CardPaymentsResponse | void>;
  postCardPaymentsReissued: ({
    body,
  }: CardPaymentsReissuedOptions) => Promise<CardPaymentsReissuedResponse | void>;
  saveCreditCard: ({
    body,
  }: SaveCardPayments) => Promise<SavedCardResponse | void>;
};

const CardPaymentsContext = createContext<CardPaymentsContextType>(
  {} as CardPaymentsContextType,
);

const useCardPaymentsSettings = (): CardPaymentsContextType =>
  useContext(CardPaymentsContext);

const PaymentsProvider = ({ children }: Props): JSX.Element => {
  const { showError } = useError();
  const { client } = useClient();
  const { application } = useApplication();
  const { selectedLoanExtension } = useLatestLoan();
  const { fetchActiveCard } = useActiveCard();

  const registerDatadog = ({
    error,
    method,
    redirectionUrl,
  }: {
    error?: AxiosError;
    method: 'card' | 'bizum' | 'reissue';
    redirectionUrl?: string;
  }) => {
    if (error) {
      datadogLogs.logger.error(
        `payment - method:${method} - client:${client?.id} - app:${application?.id}`,
        {
          error: error,
        },
      );
    } else {
      datadogLogs.logger.info(
        `payment - method:${method} - client:${client?.id} - app:${application?.id} - redirection:${redirectionUrl}`,
      );
    }
  };

  const getBizumPayments = useCallback(
    async ({
      isExtension = false,
      body,
    }: BizumPaymentsOptions): Promise<BizumPaymentsResponse | void> => {
      try {
        // replace amount property by the extension term
        if (isExtension && selectedLoanExtension) {
          delete body?.amount;
          body.extensionTerm = selectedLoanExtension?.term;
        }

        const { data } = await axiosInstance.post(
          `/payment-platform/pay-by-link/pay${isExtension ? '-extension' : ''}`,
          {
            requesterName: 'frontend',
            ...body,
          },
        );

        registerDatadog({
          method: 'bizum',
          redirectionUrl: data?.payformUrl,
        });

        return data;
      } catch (e) {
        if (!axios.isAxiosError(e)) {
          showError(e as Error);
        } else {
          registerDatadog({ method: 'bizum', error: e });
        }
        // Don't throw error otherwise
      }
    },
    [showError, selectedLoanExtension],
  );

  const postCardPayments = useCallback(
    async ({
      body,
    }: CardPaymentsOptions): Promise<CardPaymentsResponse | void> => {
      try {
        const { data } = await axiosInstance.post('/cardpayments', body, {
          headers: {
            Accept: 'application/vnd.cardpayments.v1+json',
            'Content-Type': 'application/vnd.cardpayments.v1+json',
          },
        });

        registerDatadog({
          method: 'card',
          redirectionUrl: data?.payformUrl || data?.redirectUrl,
        });

        return data;
      } catch (e) {
        if (!axios.isAxiosError(e)) {
          showError();
          throw e;
        } else {
          registerDatadog({ method: 'card', error: e });
          const errorMessageTemplate =
            e.response?.data.errors?.[0]?.messageTemplate;
          return {
            status: 'ERROR',
            errorMessageTemplate,
          };
        }
        // Don't throw error otherwise
      }
    },
    [showError],
  );

  const postCardPaymentsReissued = async ({
    body,
  }: CardPaymentsReissuedOptions): Promise<CardPaymentsReissuedResponse | void> => {
    try {
      const { data } = await axiosInstance.post(
        '/cardpayments/reissue-with-stored-card',
        body,
        {
          headers: {
            Accept: 'application/vnd.cardpayments.v1+json',
            'Content-Type': 'application/vnd.cardpayments.v1+json',
          },
        },
      );

      registerDatadog({
        method: 'reissue',
        redirectionUrl: data?.payformUrl || data?.redirectUrl,
      });

      return data;
    } catch (e) {
      if (!axios.isAxiosError(e)) {
        showError();
        throw e;
      } else {
        registerDatadog({ method: 'reissue', error: e });
        const errorMessageTemplate =
          e.response?.data.errors?.[0]?.messageTemplate;
        return {
          status: 'ERROR',
          errorMessageTemplate,
        };
      }
      // Don't throw error otherwise
    }
  };

  const saveCreditCard = useCallback(
    async ({ body }: SaveCardPayments): Promise<SavedCardResponse | void> => {
      const bodyParsed = {
        ...body,
        username: client?.fullName,
        clientNumber: client?.number,
        applicationName: `${import.meta.env.VITE_MYMOID_PRODUCT}`,
      };
      try {
        const { data } = await axiosInstance.post(
          '/cardpayments/store-credit-card',
          bodyParsed,
          {
            headers: {
              Accept: 'application/vnd.cardpayments.v1+json',
              'Content-Type': 'application/vnd.cardpayments.v1+json',
            },
          },
        );

        try {
          fetchActiveCard();
        } catch {
          // don't do nothing
        }

        return data;
      } catch (e) {
        if (!axios.isAxiosError(e)) {
          showError();
          throw e;
        }
        // Don't throw error otherwise
      }
    },
    [showError, client],
  );

  const CardPaymentsContextValue = useMemo(
    () => ({
      getBizumPayments,
      postCardPayments,
      postCardPaymentsReissued,
      saveCreditCard,
    }),
    [
      getBizumPayments,
      postCardPayments,
      postCardPaymentsReissued,
      saveCreditCard,
    ],
  );

  return (
    <CardPaymentsContext.Provider value={CardPaymentsContextValue}>
      {children}
    </CardPaymentsContext.Provider>
  );
};

export { useCardPaymentsSettings as default, PaymentsProvider };
